class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items') || this.closest('mini-cart-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItemDetailsButton extends HTMLElement {
  constructor() {
    super();

    this.itemNameRow = this.closest('.cart-item__name-row');
    this.button = this.querySelector('button.cart-item__name');
    this.button.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    event.preventDefault();
    this.itemNameRow.classList.toggle('mobile-details-hidden');
  }
}

customElements.define('cart-item-details-button', CartItemDetailsButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus') ||
      document.getElementById('MiniCart-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    document.addEventListener('LensAdvizor:postAddToCart', this.onCartUpdate.bind(this));
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('LensAdvizor:postAddToCart', this.onCartUpdate.bind(this));
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.mini-cart__inner',
      }
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const cartItem =
      this.querySelector(`#CartItem-${line}`) ||
      this.querySelector(`#CartDrawer-Item-${line}`) ||
      this.querySelector(`#MiniCart-Item-${line}`);
    const bundleId = cartItem ? cartItem.dataset.cartItemBundleId : undefined;
    const newQuantity = parseInt(quantity, 10);
    if (bundleId && newQuantity === 0) {
      const key = cartItem.dataset.cartItem;
      const updates = { [key]: newQuantity };
      this.querySelectorAll(`[data-cart-item-bundle-id="${CSS.escape(bundleId)}"]`).forEach((item) => {
        if (item.dataset.cartItem !== key) {
          updates[item.dataset.cartItem] = newQuantity;
        }
      });
      return this.updateMultipleQuantities(updates, event);
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`) || document.getElementById(`MiniCart-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          const cartDrawerWrapper = document.querySelector('cart-drawer');
          this.renderCartState(parsedState);

          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`) || document.getElementById(`MiniCart-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors') || document.getElementById('MiniCart-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  /**
   * Applies the empty-cart state and swaps in the section markup returned by the Cart AJAX API.
   * Shared by updateQuantity and updateMultipleQuantities.
   *
   * @param {Object} parsedState - Parsed cart JSON, including a `sections` object.
   */
  renderCartState(parsedState) {
    const isEmpty = parsedState.item_count === 0;
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');
    const cartQuantity = document.querySelector('.cart-total-product-quantity');
    const mainContent = document.querySelector('#MainContent');

    this.classList.toggle('is-empty', isEmpty);
    if (mainContent) mainContent.classList.toggle('main-empty-cart', isEmpty);
    if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);
    if (cartQuantity) cartQuantity.innerHTML = parsedState.item_count;

    this.getSectionsToRender().forEach((section) => {
      const updatedContents = this.getSectionInnerHTMLs(parsedState.sections[section.section], section);

      // Try to find elements by selector
      let elementsToReplace = [];
      if (section.selector) {
        elementsToReplace = document.getElementById(section.id)?.querySelectorAll(section.selector);
      }

      // Fallback if no matches or no selector
      if (!elementsToReplace || elementsToReplace.length === 0) {
        const fallbackElement = document.getElementById(section.id);
        if (fallbackElement) {
          elementsToReplace = [fallbackElement];
        }
      }

      elementsToReplace.forEach((element, index) => {
        if (updatedContents[index]) {
          element.innerHTML = updatedContents[index];
        }
      });
    });
  }

  /**
   * Resolves a line item key back to its 1-based line number, so the existing
   * enableLoading/disableLoading spinners can be targeted.
   *
   * @param {String} key - A line item key, as rendered into `data-cart-item`.
   * @returns {Number|null} The line number, or null when the key is not on the page.
   */
  getLineByKey(key) {
    const item = this.querySelector(`[data-cart-item="${CSS.escape(String(key))}"]`);
    const line = item?.dataset.line;
    return line ? parseInt(line, 10) : null;
  }

  /**
   * Updates the quantity of several line items in a single request.
   *
   * `/cart/change.js` (used by updateQuantity) handles one line per call, so updating N lines means
   * N sequential round trips and N full cart re-renders. Worse, line numbers are positional: the
   * moment one update sets a quantity to 0 the line shifts and every queued line number is stale,
   * so a batch built up front silently targets the wrong rows. `/cart/update.js` applies the whole
   * batch at once against stable line item keys and returns the re-rendered sections one time.
   *
   * @param {Object} updates - Map of line item key (`data-cart-item`) or variant id -> new quantity.
   *                           0 removes the line. Anything not listed is left untouched.
   * @param {Event} [event] - Originating event, used for CartPerformance timing.
   * @param {Object} [options]
   * @param {String} [options.variantId] - Forwarded on the cartUpdate pub/sub event.
   * @returns {Promise<Object|null>} The updated cart state, or null if nothing was sent or it failed.
   */
  async updateMultipleQuantities(updates, event, options = {}) {
    const keys = Object.keys(updates || {});
    if (keys.length === 0) return null;

    // Best effort: keys given as variant ids won't resolve to a row, and simply get no spinner.
    const lines = keys.map((key) => this.getLineByKey(key)).filter((line) => line !== null);
    lines.forEach((line) => this.enableLoading(line));

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      const parsedState = await response.json();

      // /cart/update.js reports failures as a 422 with a `description`, not an `errors` key.
      if (!response.ok) {
        throw new Error(parsedState.description || parsedState.message || window.cartStrings.error);
      }

      CartPerformance.measure('batch-change:paint-updated-sections', () => {
        this.renderCartState(parsedState);
      });

      if (event) CartPerformance.measureFromEvent('batch-change:user-action', event);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: parsedState,
        variantId: options.variantId
      });

      return parsedState;
    } catch (error) {
      console.error(error);
      this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
      const errors =
        document.getElementById('cart-errors') ||
        document.getElementById('CartDrawer-CartErrors') ||
        document.getElementById('MiniCart-CartErrors');
      if (errors) errors.textContent = error.message || window.cartStrings.error;
      return null;
    } finally {
      lines.forEach((line) => this.disableLoading(line));
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`) || document.getElementById(`MiniCart-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText') || document.getElementById('MiniCart-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTMLs(html, section) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // If selector is not supplied or matches nothing, fall back to section.id
    if (!section.selector) {
      const fallback = parsedHTML.getElementById(section.id);
      return fallback ? [fallback.innerHTML] : [];
    }

    const matches = parsedHTML.querySelectorAll(section.selector);
    if (matches.length > 0) {
      return Array.from(matches).map((el) => el.innerHTML);
    }

    // If no matches, fall back to section.id
    const fallback = parsedHTML.getElementById(section.id);
    return fallback ? [fallback.innerHTML] : [];
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems') || document.getElementById('MiniCart-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    const miniCartItemElements = this.querySelectorAll(`#MiniCart-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements, ...miniCartItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems') || document.getElementById('MiniCart-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    const miniCartItemElements = this.querySelectorAll(`#MiniCart-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    miniCartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

//Call lens advisor function to ensure all frames have a lens (and vice versa) and cleanup if don't!
subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => {
	if (_lensadvizorEmbed !== undefined && _lensadvizorEmbed !== null) {
	  setTimeout(() => {
		_lensadvizorEmbed.updateCart(e.cartData);
	  });
	}
});